import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useVehiclesSocketStore, type VehicleSocket } from "../../../../../store/admin/vehiclesSocketStore";
import { useUnitsStore, updateUnit, type Unit, type UnitDto } from "../../../../../store/admin/unitsStore";
import { useDriversStore, type Driver } from "../../../../../store/admin/driversStore";
import { getTrailerById } from "../../../../../store/admin/trailersStore";
import { UnitCard } from "../../../../admin/components/sections/units";
import { UnitDetailsModal, EditUnitModal, TrailerConnectionModal } from "../../../../admin/components/sections/units/modals";
import type { Trailer } from "../../../../admin/components/sections/trailers/types";
import { getThemeClasses } from "../../../../../utils/ThemeClasses";
import { useAlert } from "../../../../../context/AlertContext";
import { useTranslation } from "react-i18next";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LogoSplash } from "../../../../../config/brandAssets";
import { driverDisplayName } from "../../../../../utils/driverName";

interface UnitsContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
}

export default function UnitsContent({
  vehicle,
  isDarkMode,
}: UnitsContentProps) {
  const [unit, setUnit] = useState<Unit | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [driver, setDriver] = useState<Driver | null>(null);
  const [showUnitDetailModal, setShowUnitDetailModal] = useState(false);
  const [showEditUnitModal, setShowEditUnitModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  
  const [trailer, setTrailer] = useState<Trailer | null>(null);
  const [showTrailerModal, setShowTrailerModal] = useState(false);

  const { getUnitByVehicleId, connectOrDisconnectTrailerOnVehicle } = useUnitsStore();
  const updateRemolque = useVehiclesSocketStore((state) => state.updateRemolque);
  const { t } = useTranslation("adminSections");
  const lang = localStorage.getItem("lang") || "es";
  const { getDriverById } = useDriversStore();
  const { showAlert } = useAlert();
  const themeClasses = getThemeClasses(isDarkMode);
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<UnitDto>({
    mode: "onBlur",
    defaultValues: {
      lang: lang,
      name: "",
      plate: "",
      brand: "",
      model: "",
      year: 0,
      type: "",
      fuelType: "",
      icon: "icon",
      status: { id: "offline", name: "offline" },
      policy_id: "",
      policy_expiry_date: "",
    },
  });

  const types = [
    { value: "car", label: t("units.type.car") },
    { value: "truck", label: t("units.type.truck") },
    { value: "trailer", label: t("units.type.trailer") },
    { value: "pickup", label: t("units.type.pickup") },
    { value: "van", label: t("units.type.van") },
    { value: "suv", label: t("units.type.suv") },
    { value: "mpv", label: t("units.type.mpv") },
    { value: "wagon", label: t("units.type.wagon") },
    { value: "coupe", label: t("units.type.coupe") },
    { value: "convertible", label: t("units.type.convertible") },
    { value: "hatchback", label: t("units.type.hatchback") },
    { value: "sedan", label: t("units.type.sedan") },
    { value: "other", label: t("units.type.other") },
  ];

  const fuelTypes = [
    { value: "gasoline", label: t("units.fields.gasoline") },
    { value: "diesel", label: t("units.fields.diesel") },
    { value: "electric", label: t("units.fields.electric") },
    { value: "hybrid", label: t("units.fields.hybrid") },
    { value: "lpg", label: t("units.fields.lpg") },
    { value: "cng", label: t("units.fields.cng") },
  ];

  const handleSelectUnit = (unitToEdit: Unit) => {
    setSelectedUnit(unitToEdit);
    setValue("name", unitToEdit.name);
    setValue("plate", unitToEdit.plate || "");
    setValue("brand", unitToEdit.brand || "");
    setValue("model", unitToEdit.model || "");
    setValue("year", unitToEdit.year || 0);
    setValue("type", unitToEdit.type || "");
    setValue("fuelType", unitToEdit.fuelType || "");
    setValue("icon", unitToEdit.icon || "icon");
    setValue("status", unitToEdit.status || { id: "offline", name: "offline" });
    setValue("policy_id", unitToEdit.policy_id || "");
    setValue("policy_expiry_date", unitToEdit.policy_expiry_date || "");
  };

  const onSubmitUnit = async (data: UnitDto) => {
    if (!selectedUnit) return;
    setLoadingSubmit(true);
    await updateUnit(
      selectedUnit.id,
      data,
      showAlert,
      setLoadingSubmit,
      t,
      () => {
        setShowEditUnitModal(false);
        setIsRefreshing(true);
        getUnitByVehicleId(vehicle.id).then((result) => {
          if (result.success && result.data) {
            setUnit(result.data);
          }
          setIsRefreshing(false);
        });
      },
      reset
    );
  };

  useEffect(() => {
    const fetchUnit = async () => {
      if (vehicle.vehicle_id) {
        setLoading(true);
        const result = await getUnitByVehicleId(vehicle.vehicle_id);
        if (result.success && result.data) {
          setUnit(result.data);
        }
        setLoading(false);
      }
    };

    fetchUnit();
  }, [vehicle.vehicle_id, getUnitByVehicleId]);

  useEffect(() => {
    const fetchDriver = async () => {
      if (vehicle.driver?.id) {
        const result = await getDriverById(vehicle.driver.id);
        if (result.success && result.data) {
          setDriver(result.data);
        }
      }
    };

    fetchDriver();
  }, [vehicle.driver?.id, getDriverById]);

  useEffect(() => {
    const fetchTrailer = async () => {
      if (vehicle.remolque_id) {
        const result = await getTrailerById(vehicle.remolque_id);
        if (result.data) {
          setTrailer(result.data as unknown as Trailer);
        }
      }
    };

    fetchTrailer();
  }, [vehicle.remolque_id]);

  const generatePDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add logo in top left corner
    try {
      const response = await fetch(LogoSplash);
      const svgText = await response.text();
      const base64Match = svgText.match(/data:image\/png;base64,([^"]+)/);
      if (base64Match) {
        const base64Data = base64Match[1];
        doc.addImage(`data:image/png;base64,${base64Data}`, "PNG", 10, 5, 25, 30);
      }
    } catch (e) {
      // Logo failed to load, continue without it
    }

    // Green line
    doc.setDrawColor(116, 250, 76);
    doc.setLineWidth(1);
    doc.line(14, 40, pageWidth - 14, 40);

    // Helper para formatear fechas (maneja tanto timestamps de Firestore como strings ISO)
    const formatDate = (dateValue: any): string => {
      if (!dateValue) return "Sin Registro";
      try {
        // Si es un timestamp de Firestore
        if (dateValue._seconds) {
          return new Date(dateValue._seconds * 1000).toLocaleDateString();
        }
        // Si es un string ISO
        if (typeof dateValue === "string") {
          return new Date(dateValue).toLocaleDateString();
        }
        return "Sin Registro";
      } catch {
        return "Sin Registro";
      }
    };

    // Title
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    // Unit info header
    doc.setFontSize(10);
    doc.text("Unidad", 14, 50);
    doc.text(unit?.name || "Sin Registro", 14, 57);

    doc.text("Marca", pageWidth / 2, 50);
    doc.text(unit?.brand || "Sin Registro", pageWidth / 2 + 30, 50);
    doc.text("Modelo", pageWidth / 2, 57);
    doc.text(unit?.model || "Sin Registro", pageWidth / 2 + 30, 57);
    doc.text("Año", pageWidth / 2, 64);
    doc.text(unit?.year?.toString() || "Sin Registro", pageWidth / 2 + 30, 64);
    doc.text("Placas", pageWidth / 2, 71);
    doc.text(unit?.plate || "Sin Registro", pageWidth / 2 + 30, 71);

    doc.text("Póliza", 14, 64);
    doc.text(unit?.policy_id || "Sin Registro", 14, 71);
    doc.text("Vencimiento Póliza", 14, 78);
    doc.text(formatDate(unit?.policy_expiry_date), 14, 85);

    // Green line
    doc.line(14, 91, pageWidth - 14, 91);

    // Driver section
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Datos del conductor", 14, 101);
    doc.setFont("helvetica", "normal");

    autoTable(doc, {
      startY: 106,
      head: [],
      body: [
        ["Nombre", driverDisplayName(driver) || "Sin Registro"],
        ["Licencia", driver?.license_id || "Sin Registro"],
        ["Expedición", formatDate(driver?.license_expedition)],
        ["Vencimiento", formatDate(driver?.license_exp)],
      ],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 80 } },
    });
    
    // Trailer section
    const trailerY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Datos del remolque", 14, trailerY);
    doc.setFont("helvetica", "normal");
    
    autoTable(doc, {
      startY: trailerY + 5,
      head: [],
      body: [
        ["Nombre", trailer?.name || "Sin Registro"],
        ["Código", trailer?.plate || "Sin Registro"],
        ["Exclusivo", trailer?.attached ? "Sí" : "No"],
      ],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 80 } },
    });
    
    doc.save(`perfil_unidad_${unit?.plate || "unidad"}.pdf`);
  };

  const handleUnitEdit = (unitToEdit: Unit) => {
    handleSelectUnit(unitToEdit);
    setShowEditUnitModal(true);
  };

  const handleUnitDetails = (unit: Unit) => {
    setSelectedUnit(unit);
    setShowUnitDetailModal(true);
  };

  const handleTrailerClick = (unit: Unit) => {
    setSelectedUnit(unit);
    setShowTrailerModal(true);
  };

  const handleConnectTrailer = async (trailerId: string) => {
    try {
      const result = await connectOrDisconnectTrailerOnVehicle(selectedUnit!.id, trailerId);
      if (result.success) {
        // Actualizar el vehiclesSocketStore con el nuevo remolque_id
        if (vehicle.id) {
          updateRemolque(vehicle.id, trailerId);
        }
        showAlert({
          title: t("units.trailer.connectSuccess"),
          icon: "success",
        });
        setShowTrailerModal(false);
        // Refresh unit data
        if (vehicle.vehicle_id) {
          const unitResult = await getUnitByVehicleId(vehicle.vehicle_id);
          if (unitResult.success && unitResult.data) {
            setUnit(unitResult.data);
          }
        }
      }
    } catch (error) {
      showAlert({
        title: t("units.trailer.connectError"),
        icon: "error",
      });
    }
  };

  const handleDisconnectTrailer = async (trailerId: string) => {
    try {
      const result = await connectOrDisconnectTrailerOnVehicle(selectedUnit!.id, trailerId);
      if (result.success) {
        // Actualizar el vehiclesSocketStore removiendo el remolque_id
        if (vehicle.id) {
          updateRemolque(vehicle.id, null);
        }
        showAlert({
          title: t("units.trailer.disconnectSuccess"),
          icon: "success",
        });
        setShowTrailerModal(false);
        setTrailer(null);
        // Refresh unit data
        if (vehicle.vehicle_id) {
          const unitResult = await getUnitByVehicleId(vehicle.vehicle_id);
          if (unitResult.success && unitResult.data) {
            setUnit(unitResult.data);
          }
        }
      }
    } catch (error) {
      showAlert({
        title: t("units.trailer.disconnectError"),
        icon: "error",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "online":
        return "text-primary-500 bg-primary-500/10";
      case "driving":
        return "text-blue-500 bg-blue-500/10";
      case "sleep":
        return "text-yellow-500 bg-yellow-500/10";
      case "stop":
      case "alert":
        return "text-red-500 bg-red-500/10";
      case "offline":
        return "text-gray-500 bg-gray-500/10";
      default:
        return "text-gray-500 bg-gray-500/10";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "online":
        return "En línea";
      case "driving":
        return "En movimiento";
      case "sleep":
        return "Reposo";
      case "stop":
        return "Detenido";
      case "alert":
        return "Alerta";
      case "offline":
        return "Desconectado";
      default:
        return "Desconocido";
    }
  };

  // Loader
  if (loading) {
    return (
      <div className={`p-3 border-t ${borderColor}`}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-primary-500 flex items-center justify-center">
            <i className="ri-truck-line text-black text-xs"></i>
          </div>
          <h4 className={`font-semibold text-xs ${themeClasses.text}`}>
            Perfil de unidad
          </h4>
        </div>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  // Sin unidad
  if (!unit) {
    return (
      <div className={`p-3 border-t ${borderColor}`}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-primary-500 flex items-center justify-center">
            <i className="ri-truck-line text-black text-xs"></i>
          </div>
          <h4 className={`font-semibold text-xs ${themeClasses.text}`}>
            Perfil de unidad
          </h4>
        </div>
        <div className={`text-center py-6 ${themeClasses.textSecondary}`}>
          <i className="ri-error-warning-line text-2xl mb-2 block"></i>
          <p className="text-xs">No se encontró información de la unidad</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`p-3 border-t ${borderColor}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-primary-500 flex items-center justify-center">
              <i className="ri-truck-line text-black text-xs"></i>
            </div>
            <h4 className={`font-semibold text-xs ${themeClasses.text}`}>
              Perfil de unidad
            </h4>
          </div>
          <button
            onClick={generatePDF}
            className="px-1 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
            title="Generar PDF"
          >
            <i className="ri-file-pdf-2-line text-white text-sm"></i>
          </button>
        </div>

        {/* Card con overlay de refreshing */}
        <div className="relative">
          {isRefreshing && (
            <div className="absolute inset-0 bg-black/30 rounded-xl flex items-center justify-center z-10">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                <span className="text-white text-xs">Actualizando...</span>
              </div>
            </div>
          )}
          <UnitCard
            unit={unit}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            getStatusColor={getStatusColor}
            getStatusText={getStatusText}
            showActions={true}
            onEdit={handleUnitEdit}
            onDetails={handleUnitDetails}
            onTrailerClick={handleTrailerClick}
          />
        </div>
      </div>

      {showUnitDetailModal && selectedUnit && (
        <UnitDetailsModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          unit={selectedUnit}
          getStatusText={getStatusText}
          onClose={() => {
            setShowUnitDetailModal(false);
            setSelectedUnit(null);
            reset();
          }}
          onEdit={() => {
            setShowUnitDetailModal(false);
            handleSelectUnit(selectedUnit);
            setShowEditUnitModal(true);
          }}
        />
      )}

      {showEditUnitModal && selectedUnit && (
        <EditUnitModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          unit={selectedUnit}
          form={{ register, handleSubmit, control, setValue, reset, formState: { errors } } as any}
          types={types}
          fuelTypes={fuelTypes}
          loadingSubmit={loadingSubmit}
          onClose={() => {
            setShowEditUnitModal(false);
            setSelectedUnit(null);
            reset();
          }}
          onSubmit={onSubmitUnit}
        />
      )}

      {showTrailerModal && selectedUnit && (
        <TrailerConnectionModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          unit={selectedUnit}
          onClose={() => {
            setShowTrailerModal(false);
            setSelectedUnit(null);
          }}
          onConnect={handleConnectTrailer}
          onDisconnect={handleDisconnectTrailer}
        />
      )}
    </>
  );
}
